import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { Box, Stack } from '@mui/system';
import {
  Card,
  Checkbox,
  useTheme,
  CardHeader,
  Typography,
  CardContent,
  FormHelperText,
  FormControlLabel
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';

/**
 * Lets an admin open Reports to team members, each limited to their own records, or to their
 * departments' when they manage one (JT1-1829).
 */
export default function TeamReportsSetting() {

  const { t } = useTranslate();
  const theme = useTheme();

  const { organization, account } = useAuthContext();

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const [teamReportsEnabled, setTeamReportsEnabled] = useState(false);

  const teamReportsSetting = useMemo(
    () => settingsData?.find((setting) => setting.setting_type === OrganizationSetting.TEAM_REPORTS_ENABLED),
    [settingsData]
  );

  useEffect(() => {
    if (teamReportsSetting) {
      setTeamReportsEnabled(teamReportsSetting.setting_value as boolean);
    }
  }, [teamReportsSetting]);

  const onTeamReportsUpdate = async (value: boolean) => {

    if (!teamReportsSetting) return;

    setTeamReportsEnabled(value);

    try {
      await updateSettings({
        organizationId: organization.id,
        settings: [{ id: teamReportsSetting.id, value }],
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      setTeamReportsEnabled(!value);
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  }

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="solar:chart-2-bold-duotone"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.team_reports.title')}
            </Typography>
          }
        />

        <CardContent>

          <Stack direction="column" justifyContent="center" sx={{ mt: 2 }} gap={2}>

            <FormControlLabel
              disabled={!canEditSettings || !teamReportsSetting}
              label={
                <Stack gap={0} sx={{ ml: 1 }}>
                  <Typography variant="body2">
                    {t('admin-organization.settings.team_reports.description')}
                  </Typography>
                  <FormHelperText sx={{ mt: 0 }}>
                    {t('admin-organization.settings.team_reports.helper')}
                  </FormHelperText>
                </Stack>
              }
              control={<Checkbox checked={teamReportsEnabled} onChange={(e) => onTeamReportsUpdate(e.target.checked)} />}
            />

          </Stack>

        </CardContent>
      </Card>
    </Box>
  )
}
